<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVisualAccount } from '../stores/visual-account.js'
import VgIcon from './trpg/icons/VgIcon.vue'

defineProps({
  inline: { type: Boolean, default: false },
})

const api = inject('api')
if (!api) throw new Error('SiteNotificationBell: api provider is required')

const { locale, t } = useI18n()
const account = useVisualAccount()
const notifications = ref([])
const open = ref(false)
const error = ref('')
const signedIn = account.isSignedIn
const unreadCount = computed(() => notifications.value.filter(item => item.read_at === null).length)
let pollTimer = null

onMounted(() => {
  pollTimer = globalThis.setInterval(refresh, 30000)
  void refresh()
})

onBeforeUnmount(() => {
  if (pollTimer !== null) globalThis.clearInterval(pollTimer)
})

watch(locale, () => void refresh())
watch(account.isSignedIn, isSignedIn => {
  open.value = false
  if (!isSignedIn) {
    notifications.value = []
    return
  }
  void refresh()
})

async function refresh() {
  if (!signedIn.value) {
    notifications.value = []
    open.value = false
    return
  }
  error.value = ''
  try {
    const result = await api.visualGame.listNotifications()
    notifications.value = result.notifications
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : String(reason)
  }
}

async function toggleOpen() {
  open.value = !open.value
  if (open.value) await refresh()
}

async function openNotification(notification) {
  if (notification.read_at === null) {
    const updated = await api.visualGame.markNotificationRead(notification.id)
    notifications.value = notifications.value.map(item => item.id === updated.id ? updated : item)
  }
  globalThis.location.assign(notificationHref(notification))
}

function notificationHref(notification) {
  switch (notification.type) {
    case 'video_generation_ready':
      if (notification.resource_type === 'intent_video') return notification.action_href
      if (!globalThis.matchMedia('(max-width: 680px)').matches) return notification.action_href
      return `/my-videos/watch?${new URLSearchParams({ videoId: notification.resource_id }).toString()}`
    case 'video_generation_failed':
      return notification.action_href
    default:
      throw new Error(`Unhandled video notification type: ${notification.type}, id: ${notification.id}`)
  }
}

function notificationLabel(notification) {
  switch (notification.type) {
    case 'video_generation_ready': return t('visualGame.runHistory.videoReady')
    case 'video_generation_failed':
      if (notification.refunded_points === null) return t('visualGame.runHistory.videoFailed')
      return t('visualGame.runHistory.videoFailedRefund', { points: notification.refunded_points.toLocaleString(locale.value) })
    default: throw new Error(`Unhandled video notification type: ${notification.type}, id: ${notification.id}`)
  }
}

function notificationStatus(notification) {
  switch (notification.type) {
    case 'video_generation_ready': return 'ready'
    case 'video_generation_failed': return 'failed'
    default: throw new Error(`Unhandled video notification type: ${notification.type}, id: ${notification.id}`)
  }
}

function notificationVideoName(notification) {
  if (notification.video_title !== null) return notification.video_title
  return notification.scenario_title
}

function formatDate(value) {
  return new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}
</script>

<template>
  <aside v-if="signedIn" class="site-notification" :class="{ 'is-inline': inline }">
    <button
      class="site-notification-trigger"
      type="button"
      :aria-expanded="open"
      :aria-label="t('visualGame.accountCenter.videos')"
      @click="toggleOpen"
    >
      <VgIcon name="bell" :size="20" />
      <b v-if="unreadCount > 0">{{ unreadCount }}</b>
    </button>
    <button v-if="open" class="site-notification-dismiss" type="button" :aria-label="t('common.close')" @click="open = false"></button>
    <section v-if="open" class="site-notification-list" role="dialog" :aria-label="t('account.account.notifications')">
      <header class="site-notification-header">
        <strong>{{ t('account.account.notifications') }}</strong>
        <button type="button" :aria-label="t('common.close')" @click="open = false">×</button>
      </header>
      <p v-if="error" class="is-error">{{ error }}</p>
      <p v-else-if="notifications.length === 0">{{ t('visualGame.accountCenter.emptyVideos') }}</p>
      <button
        v-for="notification in notifications"
        v-else
        :key="notification.id"
        type="button"
        :class="{ 'is-unread': notification.read_at === null }"
        @click="openNotification(notification)"
      >
        <strong>{{ notificationVideoName(notification) }}</strong>
        <em :data-status="notificationStatus(notification)">{{ notificationLabel(notification) }}</em>
        <span>{{ formatDate(notification.created_at) }}</span>
      </button>
    </section>
  </aside>
</template>

<style scoped>
.site-notification { position: absolute; z-index: 1000; top: 21px; right: 22px; }
.site-notification.is-inline { position: relative; top: auto; right: auto; flex: 0 0 auto; }
.site-notification-trigger { position: relative; z-index: 2; display: grid; width: 38px; height: 38px; place-items: center; border: 0; border-radius: var(--drama-radius-full); background: var(--drama-color-surface); color: var(--drama-color-text); cursor: pointer; transition: background .2s ease; }
.site-notification-trigger:hover,
.site-notification-trigger:focus-visible,
.site-notification-trigger[aria-expanded='true'] { background: var(--drama-color-surface-alt); }
.site-notification-trigger b { position: absolute; top: -5px; right: -5px; display: grid; min-width: 18px; height: 18px; place-items: center; padding-inline: 4px; border: 1px solid color-mix(in srgb, var(--drama-color-primary) 54%, var(--drama-color-border)); border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-primary) 24%, var(--drama-color-surface)); color: var(--drama-color-primary); font-size: 10px; }
.site-notification-dismiss { position: fixed; z-index: 0; inset: 0; border: 0; background: transparent; cursor: default; }
.site-notification-list { position: absolute; z-index: 1; top: calc(100% + 8px); right: 0; display: grid; width: min(340px, calc(100vw - 32px)); max-height: 420px; overflow: auto; padding: 6px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); box-shadow: var(--drama-shadow-lg); }
.site-notification-header { display: none; }
.site-notification-list > p { margin: 0; padding: var(--drama-space-xl); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); text-align: center; }
.site-notification-list > p.is-error { color: var(--drama-color-danger); }
.site-notification-list > button { display: grid; gap: 5px; padding: var(--drama-space-md); border: 0; border-bottom: 1px solid var(--drama-color-border); background: transparent; color: var(--drama-color-text); text-align: left; cursor: pointer; }
.site-notification-list > button.is-unread { background: transparent; }
.site-notification-list strong { font-size: var(--drama-font-size-sm); }
.site-notification-list em { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); font-style: normal; }
.site-notification-list em[data-status='ready'] { color: var(--drama-color-success); }
.site-notification-list em[data-status='failed'] { color: var(--drama-color-text-muted); }
.site-notification-list span { color: var(--drama-color-text-muted); font-size: 10px; }
@media (max-width: 860px) { .site-notification { top: 13px; right: 12px; } }
@media (max-width: 680px) {
  .site-notification-dismiss { z-index: 3; background: color-mix(in srgb, var(--drama-color-text) 48%, transparent); }
  .site-notification-list { position: fixed; z-index: 4; top: 0; right: 0; bottom: 0; display: grid; width: min(88vw, 360px); max-height: none; align-content: start; overflow-x: hidden; overflow-y: auto; padding: 0 0 env(safe-area-inset-bottom); border-top: 0; border-right: 0; border-bottom: 0; border-radius: 0; box-shadow: -18px 0 48px color-mix(in srgb, var(--drama-color-text) 24%, transparent); animation: notification-drawer-enter 220ms var(--drama-ease-out) both; overscroll-behavior: contain; }
  .site-notification-header { position: sticky; z-index: 1; top: 0; display: flex; min-height: 58px; align-items: center; justify-content: space-between; padding: env(safe-area-inset-top) 14px 0 18px; border-bottom: 1px solid var(--drama-color-border); background: var(--drama-color-surface); }
  .site-notification-header strong { font-size: 17px; font-weight: var(--drama-font-weight-semibold); }
  .site-notification-header button { display: grid; width: 34px; height: 34px; place-items: center; padding: 0; border: 0; border-radius: var(--drama-radius-full); background: var(--drama-color-surface-alt); color: var(--drama-color-text); font-size: 22px; line-height: 1; cursor: pointer; }
  .site-notification-list > p { padding: 32px var(--drama-space-lg); }
  .site-notification-list > button { gap: 7px; padding: 16px 18px; }
}
@keyframes notification-drawer-enter { from { transform: translateX(100%); } to { transform: translateX(0); } }
@media (max-width: 680px) and (prefers-reduced-motion: reduce) { .site-notification-list { animation: none; } }
</style>
